import React, { useState, useId, useRef, useEffect } from 'react';
import { Modal } from './Modal';
import { AvatarCropModal } from './AvatarCropModal';
import { NotificationSettingsPanel } from './notifications/NotificationSettingsPanel';
import { NotificationPrivacySetting } from './notifications/NotificationPrivacySetting';
import { normalizeNotificationPrivacy } from '../utils/notificationPrivacy';
import { NotificationPrivacy, User, handleAvatarError } from '../types';
import { resolveAvatarUrl } from '../utils/serverUrl';
import { Check, HelpCircle, History, ImagePlus, Loader2, ScrollText, Smartphone, X } from 'lucide-react';
import { ManualUpdateCheck } from './ManualUpdateCheck';
import { AutoStartToggle } from './AutoStartToggle';
import { LogoutNotificationToggle } from './LogoutNotificationToggle';
import { isTauri } from '../utils/tauri';
import { dbService } from '../services/dbService';
import { useToast } from '../contexts/ToastContext';
import { MyRemoteSessionsModal } from './remote/MyRemoteSessionsModal';
import { MyDevicesModal } from './MyDevicesModal';

interface AvatarSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUser: User;
  /**
   * 아바타 저장. 실패하면 reject한다 — 모달이 받아서 토스트를 띄우고 닫지 않는다.
   * alreadySaved: 사진 업로드 라우트가 서버 저장까지 이미 끝낸 경우(PUT 재호출 금지).
   */
  onAvatarChange: (avatarUrl: string, opts?: { alreadySaved?: boolean }) => Promise<void>;
  /**
   * 알림 내용 공개 수준을 로컬에 반영한다. 서버 저장은 NotificationPrivacySetting이 이미 끝낸
   * 뒤에 부른다(아바타의 alreadySaved 계약과 같은 방향 — 저장을 두 번 하지 않는다).
   */
  onNotificationPrivacyChange: (privacy: NotificationPrivacy) => void;
  /** 채팅 이용 안내(면책) 재열람 — 모바일은 rail 팝오버가 없어 여기가 유일한 진입점 */
  onRulesClick?: () => void;
}

// 미리 정의된 아바타 옵션들
const AVATAR_OPTIONS = [
  { id: 'avatar1', url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Felix' },
  { id: 'avatar2', url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Aneka' },
  { id: 'avatar3', url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Milo' },
  { id: 'avatar4', url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Jasper' },
  { id: 'avatar5', url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Luna' },
  { id: 'avatar6', url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Oliver' },
  { id: 'avatar7', url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Sophie' },
  { id: 'avatar8', url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Max' },
  { id: 'avatar9', url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Coco' },
  { id: 'avatar10', url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Charlie' },
  { id: 'avatar11', url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Bella' },
  { id: 'avatar12', url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Leo' },
  { id: 'avatar13', url: 'https://api.dicebear.com/7.x/bottts/svg?seed=Felix' },
  { id: 'avatar14', url: 'https://api.dicebear.com/7.x/bottts/svg?seed=Aneka' },
  { id: 'avatar15', url: 'https://api.dicebear.com/7.x/bottts/svg?seed=Milo' },
  { id: 'avatar16', url: 'https://api.dicebear.com/7.x/bottts/svg?seed=Jasper' },
  { id: 'avatar17', url: 'https://api.dicebear.com/7.x/fun-emoji/svg?seed=Felix' },
  { id: 'avatar18', url: 'https://api.dicebear.com/7.x/fun-emoji/svg?seed=Aneka' },
  { id: 'avatar19', url: 'https://api.dicebear.com/7.x/fun-emoji/svg?seed=Milo' },
  { id: 'avatar20', url: 'https://api.dicebear.com/7.x/fun-emoji/svg?seed=Jasper' },
];

export const AvatarSettingsModal: React.FC<AvatarSettingsModalProps> = ({
  isOpen,
  onClose,
  currentUser,
  onAvatarChange,
  onNotificationPrivacyChange,
  onRulesClick,
}) => {
  const fid = useId();
  const { showToast } = useToast();
  const [selectedAvatar, setSelectedAvatar] = useState(currentUser.avatar);
  const [customUrl, setCustomUrl] = useState('');
  const [showGuide, setShowGuide] = useState(false);
  /** 원격지원 기록 — 내 PC에서 있었던 원격지원(담당자는 '내가 지원한 기록'도) */
  const [remoteHistoryOpen, setRemoteHistoryOpen] = useState(false);
  const [devicesOpen, setDevicesOpen] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  /** 크롭 모달에 넘길 원본 파일 (모달이 닫히면 null) */
  const [cropFile, setCropFile] = useState<File | null>(null);
  /**
   * 크롭을 마친 사진. 업로드는 '저장'을 누를 때 한다 —
   * 크롭 확정 즉시 올리면 사용자가 취소해도 서버에 파일이 남는다.
   */
  const [pendingBlob, setPendingBlob] = useState<Blob | null>(null);
  const [pendingPreview, setPendingPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // 미리보기용 objectURL 정리 (누수 방지)
  useEffect(() => {
    if (!pendingPreview) return;
    return () => URL.revokeObjectURL(pendingPreview);
  }, [pendingPreview]);

  /**
   * 이 컴포넌트는 App.tsx에서 **항상 마운트된 채 isOpen만 토글**된다 — 닫아도 언마운트되지 않아
   * state가 저절로 초기화되지 않는다. 정리하지 않으면 취소하고 다시 열었을 때 직전에 크롭한
   * 사진이 미리보기에 남아 있고, 그대로 저장까지 된다.
   * 열 때는 현재 아바타로 맞춘다(다른 기기에서 바뀐 경우 대비).
   */
  useEffect(() => {
    if (!isOpen) {
      setPendingBlob(null);
      setPendingPreview(null);
      setCropFile(null);
      setCustomUrl('');
      return;
    }
    setSelectedAvatar(currentUser.avatar);
  }, [isOpen]); // eslint-disable-line

  /** 프리셋·URL을 고르면 올리려던 사진은 버린다 (표시되는 것과 저장되는 것을 일치시킨다) */
  const chooseAvatarUrl = (url: string) => {
    setPendingBlob(null);
    setPendingPreview(null);
    setSelectedAvatar(url);
  };

  const handleFilePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // 같은 파일을 다시 골라도 change가 오도록 즉시 비운다
    e.target.value = '';
    if (!file) return;
    // SVG는 스크립트를 품을 수 있어 아바타로 허용하지 않는다(서버도 화이트리스트로 거부한다)
    if (file.type === 'image/svg+xml' || /\.svgz?$/i.test(file.name)) {
      showToast({ type: 'error', message: 'SVG 파일은 프로필 사진으로 쓸 수 없습니다.' });
      return;
    }
    if (!file.type.startsWith('image/')) {
      showToast({ type: 'error', message: '사진 파일만 선택할 수 있습니다.' });
      return;
    }
    // 원본 상한 — 크롭 결과가 아니라 브라우저가 디코딩할 원본 크기다(메모리 보호)
    if (file.size > 20 * 1024 * 1024) {
      showToast({ type: 'error', message: '사진은 20MB 이하만 선택할 수 있습니다.' });
      return;
    }
    setCropFile(file);
  };

  const handleCropConfirm = (blob: Blob) => {
    setPendingPreview(URL.createObjectURL(blob));
    setPendingBlob(blob);
    setCropFile(null);
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      if (pendingBlob) {
        // 업로드 라우트가 user_profiles 저장과 전역 브로드캐스트까지 마친 뒤 URL을 돌려준다.
        // alreadySaved로 PUT 재호출을 막는다 — 부르면 저장이 2번 나가고 순서도 보장되지 않는다.
        const url = await dbService.uploadAvatar(pendingBlob);
        setPendingBlob(null);
        setPendingPreview(null);
        await onAvatarChange(url, { alreadySaved: true });
      } else if (selectedAvatar && selectedAvatar !== currentUser.avatar) {
        // 프리셋·직접 URL — 사진 경로와 같은 에러 UX를 쓴다. 예전에는 await 없이 즉시 닫아
        // 저장 실패가 조용히 묻히고 로컬에만 남아 재로그인 시 되돌아갔다.
        await onAvatarChange(selectedAvatar);
      }
      // 아바타를 안 건드렸으면 서버를 부르지 않는다 — 이 모달은 알림·자동 시작 설정도 겸해서
      // 아바타와 무관하게 저장을 누르는 경우가 많다(불필요한 UPSERT + 전역 브로드캐스트 방지).
      onClose();
    } catch (err: any) {
      // 실패하면 모달을 닫지 않는다 — 편집한 사진을 잃지 않게
      showToast({ type: 'error', message: err?.message || '사진을 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.' });
    } finally {
      setSaving(false);
    }
  };

  const handleCustomUrlApply = () => {
    const url = customUrl.trim();
    if (!url) return;
    // 서버(PUT /api/profile/avatar)가 http(s)만 받는다. 여기서 걸러 주지 않으면
    // '적용'은 되는데 '저장'에서야 오류가 나 어디가 잘못됐는지 알기 어렵다.
    if (!/^https?:\/\//i.test(url)) {
      showToast({ type: 'error', message: 'http:// 또는 https://로 시작하는 주소를 입력해 주세요.' });
      return;
    }
    chooseAvatarUrl(url);
  };

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="프로필 아이콘 설정"
      size="md"
      footer={
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
          >
            취소
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving && <Loader2 size={14} className="animate-spin" aria-hidden="true" />}
            {saving ? '저장 중…' : '저장'}
          </button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* 현재 선택된 아바타 미리보기 */}
        <div className="flex items-center gap-4 p-4 bg-slate-50 border border-slate-100 rounded-lg">
          <img
            src={resolveAvatarUrl(pendingPreview || selectedAvatar)}
            alt="Preview"
            onError={handleAvatarError}
            className="w-16 h-16 rounded-full object-cover border-2 border-brand-600"
          />
          <div className="min-w-0">
            <p className="text-slate-800 font-medium">{currentUser.username}</p>
            <p className="text-sm text-slate-500">{currentUser.depart}</p>
            {pendingPreview && (
              <p className="text-xs text-brand-700 mt-0.5">저장을 누르면 이 사진으로 바뀝니다</p>
            )}
          </div>
        </div>

        {/* 사진 올리기 */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            사진 올리기
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFilePick}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm bg-white border border-slate-300 text-slate-700 rounded-lg hover:border-brand-600 hover:text-brand-700 transition-colors"
          >
            <ImagePlus size={16} aria-hidden="true" />
            {pendingPreview ? '다른 사진 선택' : '내 사진 선택'}
          </button>
          <p className="mt-1.5 text-xs text-slate-500">
            정사각형으로 잘라내고 효과를 넣을 수 있습니다. JPG·PNG·WEBP, 20MB 이하
          </p>
        </div>

        {/* 아바타 선택 그리드 */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-3">
            아이콘 선택
          </label>
          <div className="grid grid-cols-5 gap-3 max-h-48 overflow-y-auto p-1">
            {AVATAR_OPTIONS.map((avatar) => {
              const isSelected = !pendingPreview && selectedAvatar === avatar.url;
              return (
                <button
                  key={avatar.id}
                  onClick={() => chooseAvatarUrl(avatar.url)}
                  className={`relative p-1 rounded-lg transition-all ${isSelected
                    ? 'ring-2 ring-brand-400 bg-brand-100'
                    : 'hover:bg-slate-100'
                    }`}
                >
                  <img
                    src={resolveAvatarUrl(avatar.url)}
                    alt={avatar.id}
                    className="w-12 h-12 rounded-full mx-auto"
                  />
                  {isSelected && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-brand-600 rounded-full flex items-center justify-center">
                      <Check size={12} className="text-white" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* 커스텀 URL 입력 */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <label htmlFor={`${fid}-url`} className="text-sm font-medium text-slate-700">
              직접 URL 입력
            </label>
            <button
              onClick={() => setShowGuide(!showGuide)}
              className={`flex items-center gap-1 px-2 py-0.5 text-xs rounded-md transition-colors ${showGuide
                ? 'bg-brand-50 text-brand-600'
                : 'text-slate-400 hover:text-brand-600 hover:bg-slate-100'
                }`}
              title="사용 방법 안내"
            >
              <HelpCircle size={14} />
              <span>사용 방법</span>
            </button>
          </div>

          {/* 사용 매뉴얼 */}
          {showGuide && (
            <div className="mb-3 p-3 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600 space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-medium text-slate-800 text-sm">프로필 이미지 URL 설정 방법</span>
                <button onClick={() => setShowGuide(false)} className="text-slate-400 hover:text-slate-600">
                  <X size={14} />
                </button>
              </div>
              <div className="border-t border-slate-200 pt-2 space-y-2">
                <div>
                  <p className="font-medium text-slate-700 mb-1">1. 무료 아바타 생성 사이트</p>
                  <p className="text-slate-500 leading-relaxed">
                    아래 사이트에서 아바타를 만든 후 이미지 URL을 복사하세요:
                  </p>
                  <ul className="mt-1 space-y-0.5 text-slate-500">
                    <li>
                      <span className="text-brand-600">dicebear.com</span> — 랜덤 아바타 생성
                    </li>
                  </ul>
                </div>
                <div className="bg-white border border-slate-200 rounded p-2 mt-1">
                  <p className="font-medium text-slate-700 mb-1">예시 URL:</p>
                  <code className="text-brand-600 text-[11px] break-all">
                    https://api.dicebear.com/9.x/avataaars/svg?seed=아바타이름
                  </code>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <input id={`${fid}-url`}
              type="text"
              value={customUrl}
              onChange={(e) => setCustomUrl(e.target.value)}
              placeholder="https://example.com/avatar.png"
              className="flex-1 min-w-0 px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-800 placeholder-slate-400 text-base md:text-sm focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400 transition-colors"
            />
            <button
              onClick={handleCustomUrlApply}
              disabled={!customUrl.trim()}
              className="shrink-0 px-4 py-2 text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              적용
            </button>
          </div>
        </div>

        {/* 채팅 이용 안내 다시 보기 */}
        {onRulesClick && (
          <div>
            <div className="border-t border-slate-200 my-4" />
            <button
              onClick={onRulesClick}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-left rounded-lg hover:bg-slate-100 transition-colors"
            >
              <ScrollText size={18} className="text-slate-400 shrink-0" />
              <span className="flex-1 min-w-0">
                <span className="block text-sm text-slate-800">채팅 이용 안내</span>
                <span className="block text-xs text-slate-500">
                  메시지 보안·금지 사항·감사 안내를 다시 봅니다
                </span>
              </span>
            </button>
          </div>
        )}

        {/* 내 기기 — 기기 승인 상태·분실 기기 해제·새 기기 본인 확인 (기기 신뢰) */}
        <div>
          <div className="border-t border-slate-200 my-4" />
          <button
            type="button"
            onClick={() => setDevicesOpen(true)}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-left rounded-lg hover:bg-slate-100 transition-colors"
          >
            <Smartphone size={18} className="text-slate-400 shrink-0" />
            <span className="flex-1 min-w-0">
              <span className="block text-sm text-slate-800">내 기기</span>
              <span className="block text-xs text-slate-500">
                승인된 기기를 확인하고, 새 기기의 본인 확인이나 분실한 기기 해제를 합니다
              </span>
            </span>
          </button>
        </div>

        {/* 원격지원 기록 — 동의하고 참여한 세션이라 본인에게 그대로 보여 준다 */}
        <div>
          <div className="border-t border-slate-200 my-4" />
          <button
            type="button"
            onClick={() => setRemoteHistoryOpen(true)}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-left rounded-lg hover:bg-slate-100 transition-colors"
          >
            <History size={18} className="text-slate-400 shrink-0" />
            <span className="flex-1 min-w-0">
              <span className="block text-sm text-slate-800">원격지원 기록</span>
              <span className="block text-xs text-slate-500">
                내 PC에서 있었던 원격지원의 담당자·시간·처리 내용을 봅니다
              </span>
            </span>
          </button>
        </div>

        {/* 알림 설정.
            기기별 스위치(NotificationSettingsPanel)는 웹·PWA 전용이다 — Tauri의 최상위 스위치는
            트레이의 "알림 받기". 반면 '알림에 표시할 내용'은 계정 전체 설정이라 양쪽에 보여야
            하므로 섹션 자체는 항상 렌더한다. */}
        <div>
          <div className="border-t border-slate-200 my-4" />
          <label className="block text-sm font-medium text-slate-700 mb-2">
            알림
          </label>
          {!isTauri() && <NotificationSettingsPanel />}
          <div className={isTauri() ? undefined : 'mt-4 pt-4 border-t border-slate-100'}>
            <NotificationPrivacySetting
              value={normalizeNotificationPrivacy(currentUser.notificationPrivacy)}
              onChange={onNotificationPrivacyChange}
            />
          </div>
        </div>

        {/* 데스크톱 설정 (Tauri 전용) */}
        {isTauri() && (
          <div>
            <div className="border-t border-slate-200 my-4" />
            <label className="block text-sm font-medium text-slate-700 mb-2">
              데스크톱 설정
            </label>
            <div className="space-y-1">
              <AutoStartToggle />
              <LogoutNotificationToggle />
              <ManualUpdateCheck />
            </div>
          </div>
        )}
      </div>
    </Modal>

    {/* 크롭·필터 편집 — 설정 모달 위에 겹쳐 뜬다(Modal의 modalStack이 ESC를 맨 위만 받는다) */}
    <MyRemoteSessionsModal isOpen={remoteHistoryOpen} onClose={() => setRemoteHistoryOpen(false)} />
    <MyDevicesModal isOpen={devicesOpen} onClose={() => setDevicesOpen(false)} currentUser={currentUser} />

    <AvatarCropModal
      isOpen={!!cropFile}
      file={cropFile}
      onCancel={() => setCropFile(null)}
      onConfirm={handleCropConfirm}
    />
    </>
  );
};
